"use client";
import { ReactNode, useEffect, useRef } from "react";
import MonarchResolver from "@/monarch/MonarchResolver";
import { StatsAndSocialProofTestResult } from "@/monarch/rules/statsAndSocialProofTest/types";
import Testimonials from "@/components/Testimonials";
import Testimonial from "@/components/Testimonials/Testimonial";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { useInView } from "react-intersection-observer";
import { ElementType } from "@/constants/tagular/main";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import useTranslations from "@/i18n/useTranslations";

export interface HomepageStatsAndSocialProofProps {
  children?: ReactNode;
}

// Test variant component with element viewed tracking
function StatsAndSocialProofTestVariant({
  children,
}: {
  children?: ReactNode;
}) {
  const webElement: ElementViewed["webElement"] = {
    elementType: ElementType.Component,
    htmlId: "stats-social-proof-test",
    location: "homepage",
    text: "Stats and Social Proof Test Variant",
    position: "1",
    name: hyphenateForTagular("stats-social-proof-test-variant"),
  };

  const { elementViewed } = useElementEvents({ webElement });
  const { inView, ref } = useInView();
  const viewedRef = useRef(false);

  useEffect(() => {
    if (inView && !viewedRef.current) {
      elementViewed();
      viewedRef.current = true;
    }

    // Reset when element goes out of view
    if (!inView) {
      viewedRef.current = false;
    }
  }, [inView, elementViewed]);

  return <div ref={ref}>{children}</div>;
}

export default function HomepageStatsAndSocialProof({
  children,
  ...props
}: HomepageStatsAndSocialProofProps) {
  const t = useTranslations("Homepage");
  // Control variant - render the shortcode children normally
  const controlVariant = (
    <>
      <Testimonials
        heading={t("reviewsAreIn")}
        title={t("whatLearnersAreSaying")}
        titleAlign="left"
        variant="light"
        titleSize="large"
        showReviewHeadline={true}
      >
        <Testimonial
          id="b1a8e5cc-5b31-59a1-8d08-7ba543845d4c"
          attribution="Maggie B."
          program="TUMx Lean Six Sigma Professional Certificate"
        >
          <p>
            “<b>This course helped me think about the person I want to be.</b>{" "}
            The mental space that it afforded me allowed me to broaden my
            horizons and think about opportunities I hadn&apos;t considered
            before.”
          </p>
        </Testimonial>
        <Testimonial
          id="092c229f-e10a-5242-b51a-ff98716a8108"
          attribution="Danielle D."
          program="University of Central Florida Digital Marketing Boot Camp"
        >
          <p>
            “One employer was very interested in my boot camp experience and
            couldn&apos;t believe all I learned in just six months.{" "}
            <b>I ended up getting that job</b>.”
          </p>
        </Testimonial>
        <Testimonial
          id="e082380a-2bab-5b40-8237-a04f06e96107"
          attribution="Slim C."
          program="Oxford Executive Leadership Programme"
        >
          <p>
            “The online program helped me refocus my main tasks as a leader:{" "}
            <b>
              engaging with my team and connecting with potential stakeholders
            </b>
            .”
          </p>
        </Testimonial>
      </Testimonials>
    </>
  );

  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_STATS_SOCIAL_PROOF_TEST"
      variants={{
        legacy: controlVariant,
        test: (
          <MonarchResolver<StatsAndSocialProofTestResult>
            experimentKey="useStatsAndSocialProofTest"
            variants={{
              control: controlVariant,
              test: (
                <StatsAndSocialProofTestVariant>
                  {children}
                </StatsAndSocialProofTestVariant>
              ),
            }}
            fallbackVariant="control"
          />
        ),
      }}
      fallback="legacy"
    />
  );
}
